import { useQuery } from "@tanstack/react-query";
import type { Group, User } from "@estelle/domain";

import { useDataSource } from "@/adapters/data-source";

export interface DocsPeople {
  users: User[];
  groups: Group[];
  isPending: boolean;
  isError: boolean;
  /** A grant's principal, resolved to a name — falls back to the id. */
  principalName: (type: "user" | "group", id: string) => string;
}

/**
 * Users and groups, for turning a grant's `principalId` into a name.
 *
 * Both the revision history (an author id) and the permissions screen (a
 * grant's principal) need this same lookup, so it is fetched once here
 * rather than once per screen.
 */
export function useDocsPeople(): DocsPeople {
  const data = useDataSource();

  const usersQuery = useQuery({
    queryKey: ["docs", "people", "users"],
    queryFn: () => data.settings.listUsers({ limit: 200 }),
  });
  const groupsQuery = useQuery({
    queryKey: ["docs", "people", "groups"],
    queryFn: () => data.settings.listGroups({ limit: 200 }),
  });

  const users = usersQuery.data?.items ?? [];
  const groups = groupsQuery.data?.items ?? [];

  function principalName(type: "user" | "group", id: string): string {
    if (type === "user") {
      return users.find((user) => user.id === id)?.name ?? id;
    }
    return groups.find((group) => group.id === id)?.name ?? id;
  }

  return {
    users,
    groups,
    isPending: usersQuery.isPending || groupsQuery.isPending,
    isError: usersQuery.isError || groupsQuery.isError,
    principalName,
  };
}
